import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { StrategyItem, RoadmapSection, ConnectedTest, optimizeCategoryName } from '../../strategy-catalog.models';
import { StrategyColumns } from '@pdqdev/pdq-shared/dist/entities/StrategyCatalog';
import { StrategyOptimizationService } from '../../services/strategy-optimization.service';

@Component({
  selector: 'roadmap-section',
  standalone: false,
  templateUrl: './roadmap-section.component.html',
  styleUrl: './roadmap-section.component.scss',
})
export class RoadmapSectionComponent implements OnChanges {
  @Input() section: RoadmapSection;
  @Input() strategies: StrategyItem[] = [];
  @Input() connectedDropLists: string[] = [];

  @Output() strategyClicked = new EventEmitter<StrategyItem>();
  @Output() strategyRemoved = new EventEmitter<string>();
  @Output() sectionDeleted = new EventEmitter<number>();
  @Output() sectionRenamed = new EventEmitter<{ id: number; name: string }>();
  @Output() orderChanged = new EventEmitter<{ sectionId: number; strategyIds: string[] }>();
  @Output() strategyTransferred = new EventEmitter<{
    strategyId: string;
    fromSectionId: number;
    toSectionId: number;
    toIndex: number;
  }>();

  isEditing = false;
  editNameControl = new UntypedFormControl('');
  dropListId = '';
  connectedTestMap = new Map<string, ConnectedTest | null>();
  commentCountMap = new Map<string, number>();
  hasNotesMap = new Map<string, boolean>();

  readonly StrategyColumns = StrategyColumns;
  readonly optimizeCategoryName = optimizeCategoryName;
  readonly strategyDropPredicate = (drag: CdkDrag) =>
    drag.dropContainer.element.nativeElement.classList.contains('roadmap-list');

  constructor(private strategyOptimizationService: StrategyOptimizationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['section']) {
      this.dropListId = 'section-' + this.section.id;
    }
    if (changes['strategies']) {
      this.buildLookups();
    }
  }

  startEditing(): void {
    if (this.section.isDefault) return;
    this.isEditing = true;
    this.editNameControl.setValue(this.section.name);
  }

  finishEditing(): void {
    const trimmed = (this.editNameControl.value || '').trim();
    if (trimmed && trimmed !== this.section.name) {
      this.sectionRenamed.emit({ id: this.section.id, name: trimmed });
    }
    this.isEditing = false;
  }

  cancelEditing(): void {
    this.isEditing = false;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.finishEditing();
    } else if (event.key === 'Escape') {
      this.cancelEditing();
    }
  }

  onDrop(event: CdkDragDrop<{ sectionId: number; strategies: StrategyItem[] }>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.strategies, event.previousIndex, event.currentIndex);
      this.orderChanged.emit({
        sectionId: this.section.id,
        strategyIds: this.strategies.map((s) => s[StrategyColumns.ID]),
      });
    } else {
      const movedItem = event.previousContainer.data.strategies[event.previousIndex];
      this.strategyTransferred.emit({
        strategyId: movedItem[StrategyColumns.ID],
        fromSectionId: event.previousContainer.data.sectionId,
        toSectionId: this.section.id,
        toIndex: event.currentIndex,
      });
    }
  }

  removeStrategy(index: number): void {
    const removedItem = this.strategies[index];
    this.strategyRemoved.emit(removedItem[StrategyColumns.ID]);
  }

  private buildLookups(): void {
    this.connectedTestMap.clear();
    this.commentCountMap.clear();
    this.hasNotesMap.clear();
    for (const item of this.strategies) {
      const id = item[StrategyColumns.ID];
      this.connectedTestMap.set(id, this.strategyOptimizationService.getConnectedTest(id));
      this.commentCountMap.set(id, this.strategyOptimizationService.getCommentCount(id));
      this.hasNotesMap.set(id, !!item[StrategyColumns.NOTES]);
    }
  }
}
